import { useMemo } from 'react';
import { useCurrentUser } from './session.js';

/**
 * Abas de navegacao (spec 8).
 *
 * `NAV_ITEMS` e o catalogo e a ordem padrao; o que aparece, e em que ordem, sai
 * de `users.settings.tabs` via `visibleNavItems`. A barra e persistente: a
 * spec 8 proibe esconder navegacao em hamburguer no mobile.
 *
 * As cinco abas estao entregues desde a fase 6. O campo `phase` que existia
 * aqui alimentava a tela de espera das rotas ainda nao construidas, e saiu
 * junto com ela — um dado que nao muda mais nada seria so ruido a manter.
 */
export interface NavItem {
  id: string;
  path: string;
  label: string;
  /** Glifo da spec 8 — substituido por icones proprios quando houver. */
  glyph: string;
}

export const NAV_ITEMS: readonly NavItem[] = [
  { id: 'tasks', path: '/tarefas', label: 'Tarefas', glyph: '✓' },
  { id: 'calendar', path: '/calendario', label: 'Calendário', glyph: '📅' },
  { id: 'matrix', path: '/matriz', label: 'Matriz', glyph: '▦' },
  { id: 'focus', path: '/foco', label: 'Foco', glyph: '◉' },
  { id: 'more', path: '/mais', label: 'Mais', glyph: '⋯' },
] as const;

/**
 * A aba que nao pode sair da barra.
 *
 * Os Ajustes moram em "Mais" — inclusive o que escolhe as abas. Esconde-la
 * deixaria a configuracao alcancavel so pela URL ou pela paleta, e quem nao
 * conhece nenhuma das duas ficaria sem caminho de volta.
 */
export const ABA_FIXA = 'more';

/**
 * As abas visiveis, na ordem escolhida.
 *
 * `settings.tabs` e validado so como lista de strings (`userSettingsSchema`),
 * entao aqui vale o que o catalogo conhece: id desconhecido ou repetido e
 * ignorado, em vez de virar aba quebrada. "Mais" entra no fim quando falta.
 */
export function visibleNavItems(tabs: readonly string[] | undefined): NavItem[] {
  const porId = new Map(NAV_ITEMS.map((item) => [item.id, item]));
  const vistos = new Set<string>();
  const visiveis: NavItem[] = [];

  for (const id of tabs ?? NAV_ITEMS.map((item) => item.id)) {
    const item = porId.get(id);
    if (!item || vistos.has(id)) continue;
    vistos.add(id);
    visiveis.push(item);
  }

  if (!vistos.has(ABA_FIXA)) visiveis.push(porId.get(ABA_FIXA)!);
  return visiveis;
}

/**
 * Abas visiveis do usuario da sessao — a fonte da barra, da sidebar e de `1`-`5`.
 *
 * Memoizado pela preferencia: um array novo a cada render reinstalaria o
 * listener de teclado de `GlobalShortcuts` e refaria as opcoes da paleta a
 * cada tecla digitada nela.
 */
export function useNavItems(): NavItem[] {
  const tabs = useCurrentUser().settings.tabs;
  return useMemo(() => visibleNavItems(tabs), [tabs]);
}
